import { useMemo } from 'react'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  LabelList,
} from 'recharts'

interface Props {
  data: any[]
}

const renderCustomizedLabel = (props: any) => {
  const { x, y, width, height, value } = props
  if (width < 25 || !value) return null

  return (
    <text
      x={x + width / 2}
      y={y + height / 2}
      fill="#ffffff"
      textAnchor="middle"
      dominantBaseline="central"
      fontSize={11}
      fontWeight={600}
    >
      {value}%
    </text>
  )
}

function truncateText(str: string, maxLen = 14) {
  if (!str) return ''
  return str.length > maxLen ? `${str.slice(0, maxLen)}...` : str
}

export function StackedReviewChart({ data }: Props) {
  const chartData = useMemo(() => {
    const list = Array.isArray(data) ? data : []

    return list.map((d) => {
      const name = d.name || d.robotName || d.unitName || 'N/A'
      const satisfied = d.satisfiedRatings ?? d.satisfiedCount ?? 0
      const normal = d.neutralRatings ?? d.neutralCount ?? 0
      const unsatisfied = d.unsatisfiedRatings ?? d.unsatisfiedCount ?? 0

      const total = d.totalRatings || satisfied + normal + unsatisfied || 1

      const satPct = Math.round((satisfied / total) * 100)
      const normPct = Math.round((normal / total) * 100)
      const unsatPct = Math.max(0, 100 - satPct - normPct)

      return {
        fullName: name,
        name: truncateText(name, 14),
        satisfied: satPct,
        totalSatisfied: satisfied,
        normal: normPct,
        totalNormal: normal,
        unsatisfied: unsatPct,
        totalUnsatisfied: unsatisfied,
        totalRatings: d.totalRatings || satisfied + normal + unsatisfied,
      }
    })
  }, [data])

  const colors = {
    satisfied: '#21926B',
    normal: '#F3C409',
    unsatisfied: '#FF383C',
  }

  return (
    <div className="flex h-full w-full flex-col rounded-2xl border border-solid border-gray-300 bg-white p-5 shadow-sm">
      <div className="mb-4 ml-3 flex items-center justify-between">
        <h3 className="text-[18px] font-bold uppercase text-[#1F2937]">
          TỈ LỆ ĐÁNH GIÁ THEO ROBOT
        </h3>
      </div>

      <div className="min-h-[250px] w-full flex-1">
        <ResponsiveContainer
          width="100%"
          height="100%"
          initialDimension={{ width: 340, height: 280 }}
        >
          <BarChart
            layout="vertical"
            data={chartData}
            margin={{ top: 0, right: 10, left: -10, bottom: 10 }}
            barGap={0}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              horizontal={false}
              vertical={true}
              stroke="#E5E7EB"
            />

            <XAxis
              type="number"
              domain={[0, 100]}
              ticks={[0, 25, 50, 75, 100]}
              tickFormatter={(val) => `${val}%`}
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 11, fill: '#6B7280', dy: 5 }}
            />

            <YAxis
              type="category"
              dataKey="name"
              tick={{ fontSize: 12, fill: '#374151', fontWeight: 500 }}
              axisLine={false}
              tickLine={false}
              width={110}
            />

            <Tooltip
              cursor={false}
              content={({ active, payload }) => {
                if (active && payload.length) {
                  const item = payload[0].payload
                  return (
                    <div className="rounded-lg border border-gray-100 bg-white p-3 shadow-md text-xs">
                      <p className="mb-2 font-bold text-gray-800">
                        {item.fullName} (Tổng: {item.totalRatings})
                      </p>
                      <p className="text-[#21926B]">
                        Hài lòng: {item.totalSatisfied}
                      </p>
                      <p className="text-[#F3C409]">
                        Bình thường: {item.totalNormal}
                      </p>
                      <p className="text-[#FF383C]">
                        Không hài lòng: {item.totalUnsatisfied}
                      </p>
                    </div>
                  )
                }
                return null
              }}
            />

            <Bar
              dataKey="satisfied"
              name="Hài lòng"
              stackId="a"
              fill={colors.satisfied}
              barSize={20}
              radius={[6, 0, 0, 6]}
              cursor="pointer"
            >
              <LabelList dataKey="satisfied" content={renderCustomizedLabel} />
            </Bar>

            <Bar
              dataKey="normal"
              name="Bình thường"
              stackId="a"
              fill={colors.normal}
              cursor="pointer"
            >
              <LabelList dataKey="normal" content={renderCustomizedLabel} />
            </Bar>

            <Bar
              dataKey="unsatisfied"
              name="Không hài lòng"
              stackId="a"
              fill={colors.unsatisfied}
              radius={[0, 6, 6, 0]}
              cursor="pointer"
            >
              <LabelList
                dataKey="unsatisfied"
                content={renderCustomizedLabel}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="mt-2 flex justify-center gap-6 text-xs font-medium">
        <div className="flex items-center gap-1.5 text-[#21926B]">
          <span className="h-3 w-3 rounded-sm bg-[#21926B]" />
          <span>Hài lòng</span>
        </div>
        <div className="flex items-center gap-1.5 text-[#F3C409]">
          <span className="h-3 w-3 rounded-sm bg-[#F3C409]" />
          <span>Bình thường</span>
        </div>
        <div className="flex items-center gap-1.5 text-[#FF383C]">
          <span className="h-3 w-3 rounded-sm bg-[#FF383C]" />
          <span>Không hài lòng</span>
        </div>
      </div>
    </div>
  )
}
